import { Link } from 'react-router-dom';
import OpenInClassic from './OpenInClassic.jsx';
import TestBadge from '@/components/shared/TestBadge.jsx';
import FactLine from './FactLine.jsx';
import { ENTITY_SOURCE_WORDS, ROLE_WORDS, NOT_RECORDED, word, refKey } from './words.js';

// THE ACCOUNT HEADER: name, entity type with its source word when derived,
// the address (or its state word, or the address editor in place when AIRgus
// says it is missing), the people as role chips, related accounts as links
// to their own v3 page. Reads the payload; derives nothing. In compact mode
// (the phone column) the entity type, name and address rows render only when
// an AIRgus item sits on them; the facts whose rows are hidden share one
// muted line, so the header is short, every open item still lands in place,
// and no fact prints twice.

function addressText(a) {
  if (!a) return null;
  const line1 = [a.street, a.street2].filter(Boolean).join(', ');
  const line2 = [a.city, a.state].filter(Boolean).join(', ') + (a.zip ? ` ${a.zip}` : '');
  const s = [line1, line2.trim()].filter(Boolean).join(' · ');
  return s || null;
}

export default function AccountHeader({ account, people = [], related = [], byRef, ctx, onSaved, remedyHref, compact = false, payload = null }) {
  const et = account.entity_type || {};
  const addr = account.address || {};
  const addrItems = byRef.get(refKey({ subject: 'account', id: account.id, field: 'address' })) || [];
  const nameItems = byRef.get(refKey({ subject: 'account', id: account.id, field: 'name' })) || [];
  const typeItems = byRef.get(refKey({ subject: 'account', id: account.id, field: 'entity_type' })) || [];
  const addressWord = addr.needs_address ? <span className="text-warning" data-state-word>address withheld until a document settles it</span> : addressText(addr);
  const show = (items) => !compact || items.length > 0;
  return (
    <header className="rounded-card border border-border bg-surface px-4 py-3" data-account-header data-compact={compact ? 'true' : 'false'}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-xl font-semibold text-cream" data-account-name>{account.name}</h1>
        {account.is_test === true ? <TestBadge isTest /> : null}
        {account.party_role && account.party_role !== 'insured' ? <span className="rounded-pill border border-border px-2 py-0.5 text-[11px] text-text-muted">{account.party_role}</span> : null}
        {account.needs_real_name ? <span className="text-xs text-warning">needs a real name</span> : null}
        {/* The classic twin of this screen, from the one table (lib/v3-classic.js); replaces the link this header used to build by hand. */}
        <OpenInClassic payload={payload} className="ml-auto" />
      </div>
      {compact && (typeItems.length === 0 || addrItems.length === 0) ? (
        <p className="mt-1 text-sm text-text-muted" data-compact-facts>
          {typeItems.length === 0 ? <span data-entity-source>{et.value || <span className="text-text-dim" data-state-word>entity type {NOT_RECORDED}</span>}</span> : null}
          {typeItems.length === 0 && addrItems.length === 0 ? <span className="mx-2 text-text-dim">·</span> : null}
          {addrItems.length === 0 ? (addressWord || <span className="text-text-dim" data-state-word>address {NOT_RECORDED}</span>) : null}
        </p>
      ) : null}
      <div className="mt-2">
        {show(typeItems) ? <FactLine label="Entity type" fact_ref={{ subject: 'account', id: account.id, field: 'entity_type' }} items={typeItems} ctx={ctx} onSaved={onSaved} remedyHref={remedyHref}
          value={et.value ? <>{et.value}{et.source ? <span className="ml-2 text-xs text-text-dim" data-entity-source>{word(ENTITY_SOURCE_WORDS, et.source)}</span> : null}</> : null} /> : null}
        {show(nameItems) ? <FactLine label="Name" fact_ref={{ subject: 'account', id: account.id, field: 'name' }} items={nameItems} ctx={ctx} onSaved={onSaved} remedyHref={remedyHref} value={account.name} /> : null}
        {show(addrItems) ? <FactLine label="Address" fact_ref={{ subject: 'account', id: account.id, field: 'address' }} items={addrItems} ctx={ctx} onSaved={onSaved} remedyHref={remedyHref}
          editorValue={{ street: addr.street, street_2: addr.street2, city: addr.city, state: addr.state, zip: addr.zip }}
          value={addressWord} /> : null}
        <FactLine label="People" value={people.length ? (
          <span className="flex flex-wrap gap-1.5" data-people-chips>
            {people.map((p) => (
              <a key={p.id} href={`#person-${p.id}`} className="inline-flex min-h-[36px] items-center gap-1 rounded-pill border border-border px-2.5 py-0.5 text-xs text-text hover:border-border-strong">
                <span className="font-medium">{p.display_name}</span>
                <span className="text-text-dim">{(p.roles || []).filter((r) => !r.ended_at).map((r) => word(ROLE_WORDS, r.role)).join(', ') || NOT_RECORDED}</span>
              </a>
            ))}
          </span>
        ) : null} />
        <FactLine label="Related" value={related.length ? (
          <span className="flex flex-wrap gap-x-3 gap-y-1" data-related-accounts>
            {related.map((r) => (
              <Link key={r.id} to={`/v3/account/${encodeURIComponent(r.id)}`} className="inline-flex min-h-[36px] items-center text-sm text-accent underline-offset-2 hover:underline" title={r.reason || ''}>{r.name}<span className="ml-1 text-xs text-text-dim">{String(r.relation || '').replace(/_/g, ' ')}</span></Link>
            ))}
          </span>
        ) : null} />
      </div>
    </header>
  );
}
